"use client";

import { makeFunctionReference } from "convex/server";
import { useMutation } from "convex/react";
import { Radar, Sparkles } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { getEmptyListState } from "@/core/ui/empty-states";
import {
  rankCarrierLanePreferences,
  type CarrierCapacityMatch,
} from "@/core/tma/dispatch";
import type { LoadDetailData } from "./load-detail-types";

const matchForLoad = makeFunctionReference<
  "mutation",
  {
    tenantId: string;
    loadId: string;
    maxResults?: number;
    actor: "dispatcher";
    actorId?: string;
  },
  { actionId: string; evidenceId: string; matches: CarrierCapacityMatch[] }
>("carrierLanePreferences:matchForLoad");

export function CapacityMatchPanel({ load }: { load: LoadDetailData["load"] }) {
  if (!process.env.NEXT_PUBLIC_CONVEX_URL) return <CapacityMatchContent load={load} />;
  return <CapacityMatchLive load={load} />;
}

function CapacityMatchLive({ load }: { load: LoadDetailData["load"] }) {
  const runMatch = useMutation(matchForLoad);
  return (
    <CapacityMatchContent
      load={load}
      onMatch={() =>
        runMatch({
          tenantId: load.tenantId,
          loadId: load._id,
          maxResults: 5,
          actor: "dispatcher",
          actorId: "current-user",
        })
      }
    />
  );
}

function CapacityMatchContent({
  load,
  onMatch,
}: {
  load: LoadDetailData["load"];
  onMatch?: () => Promise<{ matches: CarrierCapacityMatch[] }>;
}) {
  const [matches, setMatches] = useState<CarrierCapacityMatch[]>([]);
  const [status, setStatus] = useState("No preference match run");
  const [isRunning, setIsRunning] = useState(false);

  async function run() {
    setIsRunning(true);
    try {
      const result = onMatch
        ? await onMatch()
        : {
            matches: rankCarrierLanePreferences({
              load: {
                loadId: load._id,
                reference: load.reference,
                origin: load.origin,
                destination: load.destination,
                pickupWindowStart: load.pickupWindowStart,
                equipmentType: load.equipment?.type ?? load.equipmentType,
                targetRateCents: 145000,
              },
              preferences: demoPreferences,
              maxResults: 5,
            }),
          };
      setMatches(result.matches);
      setStatus(`${result.matches.length} carrier preferences ranked`);
    } finally {
      setIsRunning(false);
    }
  }

  return (
    <Card id="capacity-match">
      <CardHeader className="p-3">
        <CardTitle className="flex items-center justify-between gap-3 text-sm">
          <span className="flex items-center gap-2">
            <Radar size={15} />
            Capacity Match
          </span>
          <Badge variant={matches.length > 0 ? "good" : "info"}>{matches.length} matches</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 p-3 text-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground">{status}</div>
          <Button className="h-8" variant="secondary" onClick={run} disabled={isRunning}>
            <Sparkles size={14} />
            Match
          </Button>
        </div>
        <div className="space-y-2">
          {matches.length === 0 ? (
            <EmptyState className="p-4" onAction={run} {...getEmptyListState("capacityMatches")} />
          ) : (
            matches.map((match) => (
              <div key={match.carrierMc} className="rounded-md border border-border p-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium">{match.carrierName}</div>
                  <Badge variant={match.fitScore >= 80 ? "good" : "warn"}>{match.fitScore}</Badge>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  MC {match.carrierMc} · {formatMoney(match.preferredRateCents)}
                </div>
                <div className="mt-2 text-xs text-muted-foreground">{match.reasons.slice(0, 3).join("; ")}</div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const demoPreferences = [
  {
    carrierMc: "123456",
    carrierName: "North Star Trucking",
    originRegion: "TX",
    destinationRegion: "GA",
    preferredRateCents: 142500,
    daysPreferred: ["Wednesday", "Thursday"],
    notes: "Dedicated dry-van lane",
  },
  {
    carrierMc: "200200",
    carrierName: "Blue Ridge Freight",
    originRegion: "any",
    destinationRegion: "Southeast",
    preferredRateCents: 151000,
    daysPreferred: ["any"],
  },
];

function formatMoney(cents?: number) {
  if (!cents) return "rate open";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(cents / 100);
}
